import React, { useEffect, useState } from "react";
import ListSelect from "../../../components/Utils/ListSelect";
import { GridFooterContainer, GridFooter, DataGrid } from "@mui/x-data-grid";
import { Button, Chip } from "@mui/material";
import InstancedClassAdjustModal from "../../SPOTSurveys/SPOTOrdering/components/InstancedClassAdjustModal";
import EditClassModal from "./components/EditClassModal";
import { getRolesByClassId } from "../../../components/Api/Getters";
import { ClassIdsContext } from "../../../context/RequisitionContext";
import { useDepartmentsQuery } from "../../../Hooks/useDepartmentsQuery";
import { useTermsQueryFull } from "../../../Hooks/useTermsQuery";
import { useGetClassIdsWithBlueCourses } from "../../../Hooks/useClassIdsQuery";
import isAuthenticated from "../../../components/Utils/isAuthenticated";
import { useAtom } from "jotai";
import { termsAtom, selectedDeptAtom, selectedClassAtom } from "../../../components/Atoms/atoms";
import InstructorCell from "../../SPOTSurveys/SPOTOrdering/components/InstructorCell";
import { useQueryClient } from "react-query";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function EditClasses() {
	const auth = isAuthenticated();
	const token = auth.token;
	const deptIdsFromLDAP = auth.dept_ids || [];

	const [openInstanceModal, setOpenInstanceModal] = useState(false);
	const [openEditModal, setOpenEditModal] = useState(false);
	const [selectedDept, setSelectedDept] = useAtom(selectedDeptAtom);

	const [selectedClass, setSelectedClass] = useAtom(selectedClassAtom);
	const queryClient = useQueryClient();

	const [terms, setTerms] = useAtom(termsAtom);
	const [instancedClasses, setInstancedClasses] = useState([]);
	const [roles, setRoles] = useState([]);
	const [classIds, setClassIds] = useState([]);
	const [departmentsList, setDepartmentsList] = useState([]);

	const { data: departments } = useDepartmentsQuery(token);

	const { data: termsList } = useTermsQueryFull(token);

	const handleRefresh = () => {
		queryClient.invalidateQueries([
			"classIdObjectsByDeptAndTerm",
			selectedDept.deptId,
			terms.termNumber,
		]);
	};

	const {
		data: classIdsObjects,
		refetch,
		isLoading: isLoadingClasses,
		isFetching: isFetchingClasses,
	} = useGetClassIdsWithBlueCourses(token, selectedDept?.deptId, terms?.termNumber, {
		enabled: !!token && !!selectedDept?.deptId && !!terms?.termNumber,
		staleTime: 1000 * 5,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (deptIdsFromLDAP && deptIdsFromLDAP.length > 0 && departments && departments.length > 0) {
			// Split the string and convert each ID to a number
			const deptArray = deptIdsFromLDAP.split(",").map((deptId) => Number(deptId.trim()));

			// Take the first department ID from the array
			const firstDeptId = deptArray[0];

			// Find the matching department
			const dept = departments.find((dept) => dept.key === firstDeptId);

			if (dept) {
				setSelectedDept({ department: dept.department, deptId: firstDeptId });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deptIdsFromLDAP, departments]);

	useEffect(() => {
		if (classIdsObjects) {
			// First, add id field to all class objects
			const classIdsArray = classIdsObjects.map((classid) => ({
				...classid,
				id: classid.classid,
			}));

			// Build a map of root class ID -> array of instances
			// Root classes have original: null, instances have original: rootClassId
			const rootClasses = new Map(); // rootClassId -> root class object
			const instancesByRoot = new Map(); // rootClassId -> array of instance objects

			classIdsArray.forEach((cls) => {
				if (cls.original === null || cls.original === undefined) {
					// This is a root class
					rootClasses.set(cls.classid, cls);
					if (!instancesByRoot.has(cls.classid)) {
						instancesByRoot.set(cls.classid, []);
					}
				} else {
					// This is an instance - group by its original (root) class ID
					const rootId = cls.original;
					if (!instancesByRoot.has(rootId)) {
						instancesByRoot.set(rootId, []);
					}
					instancesByRoot.get(rootId).push(cls);
				}
			});

			// Process and build the final sorted array
			const processedRows = [];

			// Sort root classes by their class ID
			const sortedRootIds = Array.from(rootClasses.keys()).sort((a, b) => a - b);

			sortedRootIds.forEach((rootId) => {
				const rootClass = rootClasses.get(rootId);
				const instances = instancesByRoot.get(rootId) || [];

				// Sort instances by their class ID
				instances.sort((a, b) => a.classid - b.classid);

				const hasInstances = instances.length > 0;
				const instanceCount = instances.length + 1; // +1 for the root itself

				// Add the root class first
				processedRows.push({
					...rootClass,
					_isInstance: false,
					_instanceCount: instanceCount,
					_hasInstances: hasInstances,
					_rootClassId: rootId,
				});

				// Add all instances after the root
				instances.forEach((inst) => {
					processedRows.push({
						...inst,
						_isInstance: true,
						_instanceCount: 0,
						_hasInstances: false,
						_rootClassId: rootId,
					});
				});
			});

			// Handle any orphaned instances (instances whose root class doesn't exist in this dataset)
			// These would be instances where we don't have the root class loaded
			instancesByRoot.forEach((instances, rootId) => {
				if (!rootClasses.has(rootId)) {
					instances.sort((a, b) => a.classid - b.classid);
					instances.forEach((inst) => {
						processedRows.push({
							...inst,
							_isInstance: true,
							_instanceCount: 0,
							_hasInstances: false,
							_rootClassId: rootId,
							_orphanedInstance: true, // Flag for debugging/styling if needed
						});
					});
				}
			});

			setClassIds(processedRows);
		}
	}, [classIdsObjects]);

	useEffect(() => {
		if (departments && departments.length > 0) {
			//map object to list of department names
			const deptNames = departments.map((dept) => dept.department);
			setDepartmentsList(deptNames);
		}
	}, [departments]);

	useEffect(() => {
		if (termsList && termsList.length > 0) {
			const termDescriptions = termsList.map((term) => term.desc);
			const termNumbers = termsList.map((term) => term.term);
			const termsListVals = termNumbers.map((term, index) => {
				return termDescriptions[index] + " (" + term + ")";
			});

			const highestTermNumber = Math.max(...termNumbers);
			const highestTermIndex = termNumbers.indexOf(highestTermNumber);
			const highestTerm = termsListVals[highestTermIndex];
			setTerms({
				terms: termsListVals,
				termValue: highestTerm,
				termNumber: Number(highestTermNumber),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [termsList]);

	useEffect(() => {
		async function getRolesBySingleClassId() {
			if (openInstanceModal && selectedClass.classid) {
				const classid = selectedClass.classid;
				const result = await getRolesByClassId(token, classid);
				setRoles(result);
			}
		}
		getRolesBySingleClassId();
	}, [selectedClass, openInstanceModal, token]);

	const CustomFooter = () => {
		return (
			<GridFooterContainer sx={{ display: "flex", justifyContent: "flex-start" }}>
				<GridFooter sx={{ position: "absolute", right: "10px" }} />
			</GridFooterContainer>
		);
	};

	const handleSelectedRow = (row, action) => {
		setSelectedClass(row);
		if (action === "add") setOpenInstanceModal(true);
		if (action === "edit") setOpenEditModal(true);
	};

	const renderAddInstancedClasses = (params) => {
		if (params.row.original === null)
			return (
				<div>
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={() => {
							handleSelectedRow(params.row, "add");
						}}
					>
						Add
					</Button>
				</div>
			);
	};

	const renderEditInstancedClasses = (params) => {
		return (
			<div>
				<Button
					variant="contained"
					sx={{ marginRight: params.row.original ? "6px" : "0px" }}
					color="primary"
					size="small"
					onClick={() => {
						handleSelectedRow(params.row, "edit");
					}}
				>
					Edit
				</Button>
			</div>
		);
	};

	const handleTeamVisibility = (row) => {
		const teamNames = row.roles.map((role) => {
			// Corrected logic to ensure proper ternary operation
			return role.name ? `(${role.name})` : `(${role.user.last_name}, ${role.user.first_name})`;
		});
		const teamName = teamNames.join(", ");

		const updatedRows = [...classIds];
		const index = updatedRows.findIndex((r) => r.classid === row.classid);
		updatedRows[index].teamName = teamName;
		updatedRows[index].nameExpanded = !updatedRows[index].nameExpanded;

		setClassIds(updatedRows);
	};

	const renderInstructorNameTable = (params) => {
		return (
			<div className="text-left flex items-center h-full w-full">
				<InstructorCell row={params.row} handleTeamVisibility={handleTeamVisibility} />
			</div>
		);
	};

	const renderClassIdCell = (params) => {
		const { row } = params;
		const isInstance = row._isInstance;
		const instanceCount = row._instanceCount; // Total count including root

		return (
			<div className="flex items-center gap-2 w-full">
				{isInstance && <span className="text-gray-400 pl-2">↳</span>}
				<span className={isInstance ? "text-gray-600" : "font-medium"}>{params.value}</span>
				{!isInstance && instanceCount > 1 && (
					<Chip
						label={`+${instanceCount - 1} instance${instanceCount > 2 ? "s" : ""}`}
						size="small"
						color="primary"
						variant="outlined"
						sx={{ height: 20, fontSize: "0.7rem" }}
					/>
				)}
			</div>
		);
	};

	// Custom sort comparator that keeps instances grouped with their root class
	const groupedSortComparator = (v1, v2, param1, param2) => {
		const row1 = param1.api.getRow(param1.id);
		const row2 = param2.api.getRow(param2.id);

		// First, compare by root class ID to keep groups together
		const root1 = row1._rootClassId;
		const root2 = row2._rootClassId;

		// Get current sort direction
		const sortModel = param1.api.getSortModel();
		const isDescending = sortModel.length > 0 && sortModel[0].sort === "desc";

		if (root1 !== root2) {
			// Different groups - sort by the root's value of the field being sorted
			// We need to get the root row's value for proper sorting
			const rootRow1 = row1._isInstance ? param1.api.getRow(root1) : row1;
			const rootRow2 = row2._isInstance ? param2.api.getRow(root2) : row2;

			const rootVal1 = rootRow1 ? rootRow1[param1.field] : v1;
			const rootVal2 = rootRow2 ? rootRow2[param2.field] : v2;

			if (rootVal1 === rootVal2) return root1 - root2;
			if (rootVal1 == null) return 1;
			if (rootVal2 == null) return -1;
			if (typeof rootVal1 === "string") {
				return rootVal1.localeCompare(rootVal2);
			}
			return rootVal1 < rootVal2 ? -1 : 1;
		}

		// Same group - root always comes first, instances always sorted asc by classid
		// We need to counteract the descending inversion by flipping the result
		let inGroupOrder;
		if (!row1._isInstance && row2._isInstance) {
			inGroupOrder = -1; // root before instance
		} else if (row1._isInstance && !row2._isInstance) {
			inGroupOrder = 1; // instance after root
		} else {
			// Both are instances - always sort ascending by classid
			inGroupOrder = row1.classid - row2.classid;
		}

		// If descending, invert the in-group order so it stays correct after MUI inverts it
		return isDescending ? -inGroupOrder : inGroupOrder;
	};

	const columns = [
		{
			field: "classid",
			headerName: "Class ID",
			flex: 0.15,
			renderCell: renderClassIdCell,
			sortComparator: groupedSortComparator,
		},
		{
			field: "ctitle",
			headerName: "Class Title",
			flex: 0.25,
			sortComparator: groupedSortComparator,
		},

		{
			field: "csize",
			headerName: "Size",
			flex: 0.075,
			sortComparator: groupedSortComparator,
		},
		{
			field: "name",
			headerName: "Instructor Name",
			flex: 0.25,
			renderCell: renderInstructorNameTable,
			sortComparator: groupedSortComparator,
		},
		{
			field: "addinst",
			headerName: "Instance",
			flex: 0.1,
			headerAlign: "center",
			align: "center",
			renderCell: renderAddInstancedClasses,
			sortable: false,
		},
		{
			field: "edit",
			headerName: "Edit Class",
			headerAlign: "center",
			flex: 0.1,
			align: "center",
			renderCell: renderEditInstancedClasses,
			sortable: false,
		},
		{
			field: "c_edate",
			headerName: "End Date",
			flex: 0.1,
			disableClickEventBubbling: true,
			sortComparator: groupedSortComparator,
		},

		{
			field: "examdate",
			headerName: "Exam Date",
			flex: 0.1,
			disableClickEventBubbling: true,
			sortComparator: groupedSortComparator,
		},

		{
			field: "c_location",
			headerName: "Location",
			flex: 0.075,
			disableClickEventBubbling: true,
			sortComparator: groupedSortComparator,
		},
		{
			field: "teamsize",
			headerName: "# Inst",
			flex: 0.06,
			disableClickEventBubbling: true,
			sortComparator: groupedSortComparator,
		},
	];

	const handleDepartmentChange = (event) => {
		const deptName = event.target.value;
		const dept = departments.find((dept) => dept.department === deptName);
		setSelectedDept({ department: deptName, deptId: dept.key });
		refetch();
	};

	const isLoading = isLoadingClasses || !terms?.termNumber || !selectedDept?.deptId;

	return (
		<ClassIdsContext.Provider value={{ classIds, setClassIds }}>
			{/* Loading overlay for refetching */}
			<Backdrop
				open={isFetchingClasses && !isLoadingClasses}
				sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
			>
				<div className="flex flex-col items-center gap-3">
					<CircularProgress color="inherit" />
					<span className="text-white font-medium">Refreshing data...</span>
				</div>
			</Backdrop>

			<div className="flex flex-col items-center min-h-screen bg-gray-50 py-8 px-4">
				{/* Header Card */}
				<div className="w-full max-w-[95vw] mb-6">
					<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
						<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
							<div>
								<h1 className="text-2xl font-semibold text-primary">Edit / Instance Classes</h1>
								<p className="text-gray-500 mt-1">
									Manage instructors and create class instances for the selected term and
									department.
								</p>
							</div>

							{/* Filter Controls */}
							<div className="flex flex-wrap items-center gap-3">
								<Button
									variant="outlined"
									startIcon={<RefreshIcon />}
									onClick={handleRefresh}
									disabled={isLoading}
								>
									Refresh
								</Button>
								<ListSelect
									className="termSelect"
									list={terms?.terms ? terms.terms : []}
									label="Term"
									width="220px"
									size="small"
									marginBottom="0px"
									value={terms.termValue || ""}
									onChange={(e) => {
										const termNumber = e.target.value.match(/\(([^)]+)\)/)[1];
										setTerms({
											...terms,
											termNumber: Number(termNumber),
											termValue: e.target.value,
										});
									}}
								/>
								<ListSelect
									className="deptSelect"
									list={departmentsList}
									label="Department"
									width="320px"
									size="small"
									marginBottom="0px"
									value={selectedDept.department || ""}
									onChange={handleDepartmentChange}
								/>
							</div>
						</div>
					</div>
				</div>

				{/* Modals */}
				<InstancedClassAdjustModal
					open={openInstanceModal}
					setOpen={setOpenInstanceModal}
					classAdjustRows={classIds}
					handleRefresh={handleRefresh}
					instancedClasses={instancedClasses}
					setInstancedClasses={setInstancedClasses}
					roles={roles}
				/>
				<EditClassModal
					open={openEditModal}
					setOpen={setOpenEditModal}
					handleRefresh={handleRefresh}
					classIds={classIds}
				/>

				{/* Data Grid Card */}
				<div className="w-full max-w-[95vw] flex-1">
					<div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[calc(100vh-280px)] min-h-[500px]">
						{isLoading ? (
							<div className="flex flex-col items-center justify-center h-full gap-4">
								<CircularProgress size={48} sx={{ color: "#275d38" }} />
								<span className="text-gray-600 font-medium">Loading classes...</span>
							</div>
						) : (
							<DataGrid
								sx={{
									border: "none",
									"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
										outline: "none !important",
									},
									"& .MuiDataGrid-columnHeaders": {
										backgroundColor: "#f9fafb",
										borderBottom: "2px solid #e5e7eb",
										fontSize: "0.875rem",
										fontWeight: 600,
										color: "#374151",
									},
									"& .MuiDataGrid-columnHeaderTitle": {
										fontWeight: 600,
									},
									"& .MuiDataGrid-row": {
										"&:hover": {
											backgroundColor: "#f3f4f6",
										},
									},
									"& .MuiDataGrid-cell": {
										borderBottom: "1px solid #f3f4f6",
										fontSize: "0.875rem",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
										width: "8px",
										height: "8px",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
										background: "#f1f5f9",
										borderRadius: "4px",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
										backgroundColor: "#cbd5e1",
										borderRadius: "4px",
										"&:hover": {
											backgroundColor: "#94a3b8",
										},
									},
									// Instance row styling
									"& .instance-row": {
										backgroundColor: "#f0fdf4",
										borderLeft: "4px solid #275d38",
									},
									"& .instance-row:hover": {
										backgroundColor: "#dcfce7 !important",
									},
									// Parent row with instances styling
									"& .has-instances-row": {
										fontWeight: 500,
										backgroundColor: "#fefefe",
									},
									"& .has-instances-row:hover": {
										backgroundColor: "#f9fafb !important",
									},

									borderRadius: "0.5rem",
								}}
								disableSelectionOnClick
								components={{ Footer: CustomFooter }}
								rows={classIds}
								density="compact"
								columns={columns}
								initialState={{
									pagination: {
										paginationModel: { pageSize: 100, page: 0 },
									},
								}}
								getRowClassName={(params) => {
									if (params.row._isInstance) return "instance-row";
									if (params.row._hasInstances) return "has-instances-row";
									return "";
								}}
								getRowId={(row) => row.classid}
							/>
						)}
					</div>
				</div>
			</div>
		</ClassIdsContext.Provider>
	);
}
